import { type FC, useRef, useState } from 'react'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useLazyGetUsersQuery } from '../../../../store/user/user'
import { useLazySearchExternalUsersQuery } from '../../../../store/externalUser/externalUser'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { type BookingInvitee } from '../../../../models/Scheduling'
import { type User } from '../../../../models/User'
import { isValidEmail } from '../../../../common/util'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { clientDisplayName } from '../../shared/schedulingDisplay'
import { inviteeRoleLabel } from './scheduleHelpers'
import { SectionDropdownItemOption } from '../../../../components/SelectDropdown/SectionDropdownItemOption'
import { dropdownMessage, dropdownSectionHeader } from '../../../../components/SelectDropdown/dropdownOptions'

const _NO_MATCH_MESSAGE = 'No matches. Type a full email to add a guest.'

const _SEARCH_TERM_MIN_LENGTH = 2
const _SEARCH_PAGE_SIZE = 8
const _TEAM_OPTION_PREFIX = 'team:'
const _CONTACT_OPTION_PREFIX = 'contact:'
const _EMAIL_OPTION_PREFIX = 'email:'

interface InviteOthersProps {
  invitees: BookingInvitee[]
  onInviteesChange: (next: BookingInvitee[]) => void
}

const inviteeKey = (invitee: BookingInvitee): string => invitee.userId ?? invitee.contactId ?? invitee.email

const splitName = (name: string): { firstName?: string; lastName?: string } => {
  const [firstName, ...rest] = name.trim().split(/\s+/)
  return { firstName, lastName: rest.length > 0 ? rest.join(' ') : undefined }
}

const emailInviteOption = (email: string): SelectOption => ({
  id: `${_EMAIL_OPTION_PREFIX}${email}`,
  label: email,
  displayLabel: (
    <span className='d-flex flex-column'>
      <span>{email}</span>
      <span className='paragraph-small zocks-text-gray-500'>Add as a guest</span>
    </span>
  ),
})

export const InviteOthers: FC<InviteOthersProps> = ({ invitees, onInviteesChange }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [searchUsers] = useLazyGetUsersQuery()
  const [searchExternalUsers] = useLazySearchExternalUsersQuery()
  const [expanded, setExpanded] = useState(false)
  const [options, setOptions] = useState<SelectOption[]>([])
  const teamById = useRef<Map<string, User>>(new Map())
  const contactById = useRef<Map<string, User>>(new Map())

  const alreadyInvited = (predicate: (invitee: BookingInvitee) => boolean) => invitees.some(predicate)

  const addInvitee = (invitee: BookingInvitee) => {
    onInviteesChange([...invitees, invitee])
    setOptions([])
  }

  const addEmailInvitee = (email: string) => {
    const trimmed = email.trim()
    if (!isValidEmail(trimmed) || alreadyInvited((invitee) => invitee.email.toLowerCase() === trimmed.toLowerCase())) {
      return
    }
    addInvitee({ email: trimmed, role: inviteeRoleLabel('email') })
  }

  const handleSearchInputChange = async (term: string) => {
    const trimmed = term.trim()
    if (trimmed.length < _SEARCH_TERM_MIN_LENGTH) {
      setOptions([])
      return
    }
    const [teamResult, contactResult] = await Promise.all([
      searchUsers({ accountId: authenticatedUser.accountId, identityTypes: ['idp'], pageRequest: { page: 0, size: _SEARCH_PAGE_SIZE }, name: trimmed }),
      searchExternalUsers({ accountId: authenticatedUser.accountId, term: trimmed, pageRequest: { page: 0, size: _SEARCH_PAGE_SIZE }, orderBy: 'lastName' }),
    ])
    const teamUsers = teamResult.data?.data ?? []
    const contacts = contactResult.data?.data ?? []
    teamUsers.forEach((user) => teamById.current.set(user.id, user))
    contacts.forEach((user) => contactById.current.set(user.id, user))
    const teamOptions: SelectOption[] = teamUsers
      .filter((user) => !alreadyInvited((invitee) => invitee.userId === user.id))
      .map((user) => ({ id: `${_TEAM_OPTION_PREFIX}${user.id}`, label: getUserDisplayName(user) }))
    const contactOptions: SelectOption[] = contacts
      .filter((user) => !alreadyInvited((invitee) => invitee.contactId === user.id))
      .map((user) => ({
        id: `${_CONTACT_OPTION_PREFIX}${user.id}`,
        label: getUserDisplayName(user),
        displayLabel: (
          <span>
            {getUserDisplayName(user)} <span className='zocks-text-gray-500'>· {inviteeRoleLabel('contact', user.type)}</span>
          </span>
        ),
      }))
    const matchesKnownEmail =
      teamUsers.some((user) => user.email?.toLowerCase() === trimmed.toLowerCase()) ||
      contacts.some((user) => user.email?.toLowerCase() === trimmed.toLowerCase()) ||
      alreadyInvited((invitee) => invitee.email.toLowerCase() === trimmed.toLowerCase())
    const emailOption: SelectOption[] = isValidEmail(trimmed) && !matchesKnownEmail ? [emailInviteOption(trimmed)] : []
    const sections: SelectOption[] = []
    if (teamOptions.length > 0) {
      sections.push(dropdownSectionHeader('team', 'Team members'), ...teamOptions)
    }
    if (contactOptions.length > 0) {
      sections.push(dropdownSectionHeader('contacts', 'Contacts'), ...contactOptions)
    }
    if (emailOption.length > 0) {
      sections.push(dropdownSectionHeader('email', 'Add by email'), ...emailOption)
    }
    setOptions(sections.length > 0 ? sections : [dropdownMessage(_NO_MATCH_MESSAGE)])
  }

  const handleOptionSelected = (option: SelectOption) => {
    if (option.id.startsWith(_EMAIL_OPTION_PREFIX)) {
      addEmailInvitee(option.id.slice(_EMAIL_OPTION_PREFIX.length))
      return
    }
    if (option.id.startsWith(_TEAM_OPTION_PREFIX)) {
      const user = teamById.current.get(option.id.slice(_TEAM_OPTION_PREFIX.length))
      if (user) {
        addInvitee({ userId: user.id, ...splitName(getUserDisplayName(user)), email: user.email, role: inviteeRoleLabel('team-member') })
      }
      return
    }
    const contact = contactById.current.get(option.id.slice(_CONTACT_OPTION_PREFIX.length))
    if (contact) {
      addInvitee({
        contactId: contact.id,
        ...splitName(getUserDisplayName(contact)),
        email: contact.email ?? '',
        role: inviteeRoleLabel('contact', contact.type),
      })
    }
  }

  const removeInvitee = (key: string) => {
    onInviteesChange(invitees.filter((invitee) => inviteeKey(invitee) !== key))
  }

  const handleCancel = () => {
    onInviteesChange([])
    setOptions([])
    setExpanded(false)
  }

  if (!expanded) {
    return (
      <button
        type='button'
        className='btn btn-link p-0 border-0 d-inline-flex align-items-center zocks-gap-0.25 zocks-text-primary text-decoration-none align-self-start'
        onClick={() => setExpanded(true)}
      >
        <i className='bi bi-plus-circle' aria-hidden='true' />
        Invite others
      </button>
    )
  }

  return (
    <div className='d-flex flex-column zocks-gap-0.25'>
      <div className='d-flex align-items-center justify-content-between'>
        <label className='bold mb-0'>
          Also invite <span className='zocks-text-gray-500'>(optional)</span>
        </label>
        <button type='button' className='btn btn-link p-0 border-0 zocks-text-gray-600 text-decoration-none' onClick={handleCancel}>
          Cancel
        </button>
      </div>
      <div className='paragraph-small zocks-text-gray-600'>Other advisors or professionals. We won&apos;t check their availability.</div>
      {invitees.length > 0 ? (
        <div className='d-flex flex-wrap zocks-gap-0.5 mt-2'>
          {invitees.map((invitee) => {
            const key = inviteeKey(invitee)
            const name = clientDisplayName({ firstName: invitee.firstName, lastName: invitee.lastName, email: invitee.email })
            return (
              <span key={key} className='d-inline-flex align-items-center zocks-gap-0.25 badge rounded-pill zocks-bg-color-gray-200 zocks-text-gray-600'>
                {name}
                {invitee.role ? <span className='zocks-text-gray-500'>· {invitee.role}</span> : null}
                <button
                  type='button'
                  aria-label={`Remove ${name}`}
                  className='btn btn-link p-0 border-0 d-inline-flex align-items-center zocks-text-gray-600'
                  onClick={() => removeInvitee(key)}
                >
                  <i className='bi bi-x' aria-hidden='true' />
                </button>
              </span>
            )
          })}
        </div>
      ) : null}
      <SelectDropdown
        options={options}
        initialSelectedOption={null}
        placeholder='Search team members and contacts, or type an email'
        searchable
        clearDropdownOnOptionSelected
        inputAutoComplete='off'
        DropDownItemOptionComponent={SectionDropdownItemOption}
        onSearchInputChange={handleSearchInputChange}
        onSearchInputSelected={addEmailInvitee}
        onOptionSelected={handleOptionSelected}
      />
    </div>
  )
}
